// Customer Referral card on the Profile page: shows the user's referral code,
// share link, and history. Rewards are 1 LYD per side, credited on the referee's
// first delivered order.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { Copy, Share2, Gift, Users, CheckCircle2, Clock } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ar", { dateStyle: "medium" }); }
  catch { return ""; }
}

export default function ReferralCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/referrals/me");
      setState(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const inviteLink = state
    ? `${window.location.origin}/register?as=customer&ref=${state.code}`
    : "";

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    } catch { /* ignore */ }
  };

  const share = async () => {
    if (!navigator.share) {
      copy(inviteLink, "link");
      return;
    }
    try {
      await navigator.share({
        title: "طلبية — تطبيق التوصيل الليبي",
        text: `انضم إلى طلبية بكودي ${state.code} واحصل على 1 د.ل هدية ترحيب بعد أول طلب!`,
        url: inviteLink,
      });
    } catch { /* user cancelled */ }
  };

  if (loading) return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-5 animate-pulse">
      <div className="h-4 w-32 bg-gray-100 rounded"/>
      <div className="mt-4 h-16 bg-gray-100 rounded-2xl"/>
    </div>
  );
  if (!state) return null;

  return (
    <div className="rounded-3xl overflow-hidden talabya-shadow-float" data-testid="referral-card">
      {/* Hero */}
      <div
        className="relative p-5 text-white"
        style={{ background: "linear-gradient(135deg, #065F46 0%, #0F766E 55%, #0D9488 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10"/>
        <div className="absolute -bottom-14 -left-8 w-40 h-40 rounded-full bg-white/5"/>
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/90 text-sm">
              <Gift size={16}/> ادعُ أصدقاءك واربح
            </div>
            <div className="mt-1 text-xs text-white/80 leading-relaxed">
              كل صديق يُكمل أول طلب: <b>{state.reward_lyd_per_side} د.ل</b> لك + <b>{state.reward_lyd_per_side} د.ل</b> ترحيب له
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <div className="text-3xl font-black tracking-widest" style={{fontFamily:"Cairo"}} data-testid="referral-code">
                {state.code}
              </div>
            </div>
            <div className="mt-1 text-[11px] text-white/80">كود الإحالة الخاص بك</div>
          </div>
          <div className="shrink-0 bg-white/15 rounded-2xl px-3 py-2 text-center">
            <div className="text-[10px] text-white/80">أرباحك</div>
            <div className="font-black text-lg" data-testid="referral-earned">{state.total_earned_lyd.toFixed(2)}</div>
            <div className="text-[10px] text-white/80">د.ل</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white p-5 space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => copy(state.code, "code")}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-transparent hover:border-primary-brand/20 font-bold text-sm transition-colors"
            data-testid="referral-copy-code">
            <Copy size={14}/> {copied === "code" ? "تم النسخ ✓" : "نسخ الكود"}
          </button>
          <button
            onClick={() => copy(inviteLink, "link")}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-transparent hover:border-primary-brand/20 font-bold text-sm transition-colors"
            data-testid="referral-copy-link">
            <Copy size={14}/> {copied === "link" ? "تم النسخ ✓" : "نسخ الرابط"}
          </button>
          <button
            onClick={share}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90"
            data-testid="referral-share">
            <Share2 size={14}/> مشاركة
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-bold">
              <CheckCircle2 size={12}/> أصدقاء مكافؤون
            </div>
            <div className="mt-1 font-black text-xl text-emerald-800" data-testid="referral-credited">{state.credited_count}</div>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <div className="flex items-center gap-1.5 text-amber-700 text-xs font-bold">
              <Clock size={12}/> بانتظار أول طلب
            </div>
            <div className="mt-1 font-black text-xl text-amber-800" data-testid="referral-pending">{state.pending_count}</div>
          </div>
        </div>

        {/* History */}
        {state.history?.length > 0 && (
          <div className="border-t border-[var(--border)] pt-3">
            <div className="flex items-center gap-1.5 mb-2 text-sm font-black">
              <Users size={14}/> الأصدقاء المُحالون
            </div>
            <ul className="space-y-1.5">
              {state.history.slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between text-xs" data-testid={`referral-hist-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{r.referee_name}</div>
                    <div className="text-[10px] text-[var(--text-secondary)]">{fmtDate(r.created_at)}</div>
                  </div>
                  {r.status === "credited" ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-black text-[10px]">مكافأة صُرفت</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-black text-[10px]">بانتظار أول طلب</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
